import {
  Brain,
  Building2,
  KeyRound,
  LogOut,
  RotateCcw,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { APP_ROUTES } from "../routes";
import { useAuth } from "../hooks/useAuth";
import { apiRequest } from "../lib/api";

type SettingsTabKey = "account" | "memory" | "organization";
type SettingsNotice = { kind: "success" | "error"; message: string } | null;
type AgentMemoryCard = {
  id: string;
  scope: string;
  type: string;
  title: string;
  content: string;
  badPattern: string | null;
  betterPattern: string | null;
  confidence: number;
  needsConfirmation: boolean;
  updatedAt: number;
};

const tabs: Array<{
  key: SettingsTabKey;
  label: string;
  icon: typeof UserRound;
}> = [
  { key: "account", label: "Account", icon: UserRound },
  { key: "memory", label: "Memory", icon: Brain },
  { key: "organization", label: "Organization", icon: Building2 },
];

const toTitleCase = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

function NoticeBanner({ notice }: { notice: SettingsNotice }) {
  if (!notice) {
    return null;
  }

  return (
    <div
      className={`mt-4 rounded-md border px-4 py-3 text-sm ${
        notice.kind === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-red-200 bg-red-50 text-red-700"
      }`}
    >
      {notice.message}
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function AccountSettings({
  name,
  email,
  onLogout,
}: {
  name: string;
  email: string;
  onLogout: () => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<SettingsNotice>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);

    if (newPassword.length < 8) {
      setNotice({
        kind: "error",
        message: "New password must be at least 8 characters.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setNotice({
        kind: "error",
        message: "New password and confirmation must match.",
      });
      return;
    }

    setSubmitting(true);

    try {
      await betterAuthRequest("/change-password", {
        method: "POST",
        body: JSON.stringify({
          currentPassword,
          newPassword,
          revokeOtherSessions: false,
        }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setNotice({
        kind: "success",
        message: "Password updated.",
      });
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to change password.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="Profile"
        description="This workspace is tied to your Better Auth account."
      >
        <dl className="grid gap-4 md:grid-cols-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              Name
            </dt>
            <dd className="mt-2 text-sm font-medium text-slate-900">{name}</dd>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              Email
            </dt>
            <dd className="mt-2 text-sm font-medium text-slate-900">{email}</dd>
          </div>
        </dl>
      </SectionCard>

      <SectionCard
        title="Change Password"
        description="Update the password used for email and password sign-in."
      >
        <form className="grid gap-4 md:max-w-xl" onSubmit={handleSubmit}>
          <label className="grid gap-2 text-sm text-slate-700">
            Current password
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
              required
            />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            New password
            <input
              type="password"
              value={newPassword}
              minLength={8}
              onChange={(event) => setNewPassword(event.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
              required
            />
          </label>
          <label className="grid gap-2 text-sm text-slate-700">
            Confirm new password
            <input
              type="password"
              value={confirmPassword}
              minLength={8}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
              required
            />
          </label>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4" />
              {submitting ? "Updating..." : "Update password"}
            </button>
          </div>
        </form>
        <NoticeBanner notice={notice} />
      </SectionCard>

      <SectionCard
        title="Session"
        description="Sign out from the current workspace session."
      >
        <button
          type="button"
          onClick={() => void onLogout()}
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </SectionCard>
    </div>
  );
}

function MemorySettings() {
  const [memories, setMemories] = useState<AgentMemoryCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState<SettingsNotice>(null);

  useEffect(() => {
    let cancelled = false;

    const loadMemories = async () => {
      setLoading(true);
      setNotice(null);

      try {
        const payload = await apiRequest<{ memories: AgentMemoryCard[] }>(
          "/api/querygen/memory-cards",
        );
        if (!cancelled) {
          setMemories(payload.memories ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          setNotice({
            kind: "error",
            message: error instanceof Error ? error.message : "Unable to load memory.",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadMemories();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleReset = async () => {
    if (
      !window.confirm(
        "Reset learned preferences and patterns? Conversations and shared workspace knowledge will not be deleted.",
      )
    ) {
      return;
    }

    setResetting(true);
    setNotice(null);

    try {
      const payload = await apiRequest<{ success: boolean; deletedCount: number }>(
        "/api/querygen/memory-cards/reset",
        { method: "POST" },
      );
      setMemories([]);
      setNotice({
        kind: "success",
        message: `Reset ${payload.deletedCount} learned ${
          payload.deletedCount === 1 ? "memory" : "memories"
        }.`,
      });
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to reset memory.",
      });
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="Agent Memory"
        description="Review and reset personalization learned from your corrections and preferences."
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-slate-600">
              {loading ? "Loading learnings..." : `${memories.length} active learnings`}
            </div>
            <button
              type="button"
              onClick={() => void handleReset()}
              disabled={resetting || loading || memories.length === 0}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RotateCcw className="h-4 w-4" />
              {resetting ? "Resetting..." : "Reset memory"}
            </button>
          </div>

          {!loading && memories.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
              No learned preferences yet.
            </div>
          ) : null}

          {memories.length > 0 ? (
            <div className="divide-y divide-slate-200 rounded-md border border-slate-200">
              {memories.map((memory) => (
                <article key={memory.id} className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                      {memory.title}
                    </h4>
                    <span className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600">
                      {memory.type.replace(/_/g, " ")}
                    </span>
                    <span className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600">
                      {Math.round(memory.confidence * 100)}%
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {memory.content}
                  </p>
                  {memory.betterPattern ? (
                    <p className="mt-2 text-sm text-slate-500">
                      Prefer: {memory.betterPattern}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          ) : null}
        </div>
        <NoticeBanner notice={notice} />
      </SectionCard>
    </div>
  );
}

function OrganizationSettings({
  organizations,
  activeOrganizationId,
  onSwitchOrganization,
  onCreateOrganization,
}: {
  organizations: Array<{ id: string; name: string; slug: string; status: string }>;
  activeOrganizationId: string | null;
  onSwitchOrganization: (organizationId: string) => Promise<void>;
  onCreateOrganization: (name: string) => Promise<void>;
}) {
  const [newOrganizationName, setNewOrganizationName] = useState("");
  const [switching, setSwitching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<SettingsNotice>(null);

  const activeOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.id === activeOrganizationId) ?? null,
    [activeOrganizationId, organizations],
  );

  const handleSwitch = async (organizationId: string) => {
    if (!organizationId || organizationId === activeOrganizationId) {
      return;
    }

    setSwitching(true);
    setNotice(null);

    try {
      await onSwitchOrganization(organizationId);
      setNotice({ kind: "success", message: "Active organization updated." });
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to switch organization.",
      });
    } finally {
      setSwitching(false);
    }
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();

    if (!newOrganizationName.trim()) {
      setNotice({
        kind: "error",
        message: "Organization name is required.",
      });
      return;
    }

    setCreating(true);
    setNotice(null);

    try {
      await onCreateOrganization(newOrganizationName.trim());
      setNewOrganizationName("");
      setNotice({ kind: "success", message: "Organization created and selected." });
    } catch (error) {
      setNotice({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to create organization.",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="Active Organization"
        description="Select the organization that scopes agents, conversations, and access."
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px] md:items-end">
          <label className="grid gap-2 text-sm text-slate-700">
            Organization
            <select
              value={activeOrganizationId ?? ""}
              onChange={(event) => void handleSwitch(event.target.value)}
              disabled={switching}
              className="rounded-md border border-slate-200 bg-white px-3 py-2.5 outline-none transition focus:border-slate-900"
            >
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </label>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              Status
            </div>
            <div className="mt-2 text-sm font-medium text-slate-900">
              {activeOrganization?.status
                ? toTitleCase(activeOrganization.status)
                : "Active"}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Create Organization"
        description="Create a new org and switch into it immediately."
      >
        <form className="grid gap-4 md:max-w-xl" onSubmit={handleCreate}>
          <label className="grid gap-2 text-sm text-slate-700">
            Organization name
            <input
              value={newOrganizationName}
              onChange={(event) => setNewOrganizationName(event.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2.5 outline-none transition focus:border-slate-900"
              placeholder="Acme Analytics"
              required
            />
          </label>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Building2 className="h-4 w-4" />
              {creating ? "Creating..." : "Create organization"}
            </button>
          </div>
        </form>
        <NoticeBanner notice={notice} />
      </SectionCard>
    </div>
  );
}

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    user,
    logout,
    switchOrganization,
    createOrganization,
  } = useAuth();

  const requestedTab = searchParams.get("tab");
  const activeTab: SettingsTabKey =
    requestedTab === "organization"
      ? "organization"
      : requestedTab === "memory"
        ? "memory"
        : "account";

  const handleTabChange = (tab: SettingsTabKey) => {
    setSearchParams({ tab });
  };

  if (!user) {
    return null;
  }

  if (requestedTab === "users") {
    return <Navigate to={APP_ROUTES.users} replace />;
  }

  if (requestedTab === "knowledgebase") {
    return <Navigate to={APP_ROUTES.knowledgebase} replace />;
  }

  return (
    <div className="p-4 px-6 pb-10 md:px-16">
      <div className="pt-6 pb-2">
        <h2 className="text-xl tracking-tight">Settings</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Manage your account and organization.
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <nav className="w-full flex-shrink-0 lg:w-48">
          <div className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  tab.key === activeTab
                    ? "bg-[#e8f5fb] font-semibold text-[#0076a8]"
                    : "text-gray-600 hover:bg-slate-100 hover:text-gray-900"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        <div className="min-w-0 flex-1">
          {activeTab === "account" ? (
            <AccountSettings
              name={user.name}
              email={user.email}
              onLogout={logout}
            />
          ) : null}

          {activeTab === "memory" ? <MemorySettings /> : null}

          {activeTab === "organization" ? (
            <OrganizationSettings
              organizations={user.organizations}
              activeOrganizationId={user.activeOrganizationId}
              onSwitchOrganization={switchOrganization}
              onCreateOrganization={createOrganization}
            />
          ) : null}

        </div>
      </div>
    </div>
  );
}
